import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { useCallback, useMemo, useState } from "react";
import { InteractionDto, InteractionStatus } from "@/repository/models";
import {
	QueuesState,
	useQueuesStore,
} from "@/common/stores/nonpersistingStores/queues";
import { useMessage } from "@/common/messages";
import { useIntl } from "react-intl";
import {
	NavigationActions,
	TabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { useTableStyle } from "@/components/atoms/customTable";
import { useStyle } from "../../components/molecules/allWaitingInteractionTable";
import { Flex, GapSize } from "@/components/atoms/flex";
import { Button } from "@fluentui/react-components";
import TooltipWithMount from "@/components/atoms/tooltipWithMount/TooltipWithMount";
import { HandIcon } from "@/common/icons";
import { StatusCell } from "@/components/atoms/statusCell";
import Timer from "@/components/atoms/timer/Timer";
import { formatDate } from "@/common/helpers/dateHelper";
import { getInteractionStatusText } from "@/common/helpers/supervisorHelper";
import {
	assignCall,
	pickUpCall,
	pickUpEmail,
	pickUpGeneric,
	pickUpWebchat,
	pickUpWhatsApp,
	unParkCall,
} from "@/repository/queryFns";
import {
	pickUpVoicemail,
	assignVoicemail,
} from "@/repository/queryFns/voicemails";
import { assignEmail } from "@/repository/queryFns/emails";
import { assignGeneric } from "@/repository/queryFns/generics";
import { assignWhatsApp } from "@/repository/queryFns/whatsApp";
import { assignWebchat } from "@/repository/queryFns/webchat";
import {
	handleAssignInteraction,
	handlePickUpInteraction,
	interactionCanBePickedUp,
	interactionCanBeResumed,
} from "@/common/helpers";
import { InteractionType } from "@/common/types/interactionType";
import {
	ArrowForwardDownPersonRegular,
	Play20Regular,
} from "@fluentui/react-icons";
import { PopoverView } from "@/components/atoms/popover";
import { AgentSearchDialog } from "@/components/organisms/AgentSearch";
import { TransferChannelType } from "@/common/types";
import { AssignInteractionResponse } from "@/repository/models/assignInteraction";
import { useAgentInteractionState } from "@/hooks/agent";
import { unParkTextInteraction } from "@/common/helpers/parkHelper";
import { AgentState, useAgentStore } from "@/common/stores/persistingStores/agentPermissions";
import { checkIsTeamsPresenceDnd } from "@/components/organisms/interaction/checkIsTeamsPresenceDnd";

export const useWaitingInteractionActions = () => {
	const intl = useIntl();
	const queues = useQueuesStore((state: QueuesState) => state.queues);
	const availability = useAgentStore((state: AgentState) => state.availability);
	const [assignToAgentPopoverOpen, setAssignToAgentPopoverOpen] = useState<
		string | null
	>(null);
	const { show: showMessage } = useMessage();
	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);
	const tableClasses = useTableStyle();
	const classes = useStyle();

	const assignWaitingInteractionEnabled = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.assignWaitingInteractionEnabled
	);

	const {
		agentHasCallInProgressOrTextInOffering,
		agentHasInteractionInOffering,
	} = useAgentInteractionState();

	const showErrorMessage = useCallback(
		(message: string) => {
			showMessage("allInteractions", {
				intent: "error",
				text: intl.formatMessage({
					id: message,
				}),
			});
		},
		[showMessage, intl] //#warning-ts: to check if this is really necessary
	);

	const getQueue = useCallback(
		(queueId: string) => {
			return queues.find((x) => x.id === queueId);
		},
		[queues]
	);

	const interactionCanBeAssignedToAgent = (interaction: InteractionDto) => {
		const interactionQueue = getQueue(interaction.queueId);
		if (!interactionQueue) {
			return false;
		}

		const hasValidStatus =
			interaction.status !== InteractionStatus.Offering &&
			interaction.status !== InteractionStatus.CallbackRequest &&
			interaction.status !== InteractionStatus.CallbackOffering &&
			interaction.status !== InteractionStatus.Parked;

		return (
			hasValidStatus &&
			(interactionQueue.isSupervisor ||
				(interaction.type !== InteractionType.Voicemail &&
					interactionQueue.isOperator &&
					interactionQueue.hasAllInteractionVisibilityRight &&
					assignWaitingInteractionEnabled))
		);
	};

	const afterPickUpCallBack = useCallback(
		(isPickedUp: boolean) => {
			if (isPickedUp) {
				setSelectedTab(TabType.Interactions);
			} else {
				showErrorMessage("ErrorMessage.Pickup.InteractionUnavailable");
			}
		},
		[setSelectedTab, showErrorMessage]
	);

	const commonErrorCallBack = useCallback(() => {
		showErrorMessage("Common.Error");
	}, [showErrorMessage]);

	const pickUpFnMap = useMemo(
		() => ({
			[InteractionType.Call]: pickUpCall,
			[InteractionType.Generic]: pickUpGeneric,
			[InteractionType.Voicemail]: pickUpVoicemail,
			[InteractionType.Email]: pickUpEmail,
			[InteractionType.WhatsApp]: pickUpWhatsApp,
			[InteractionType.Webchat]: pickUpWebchat,
		}),
		[]
	);

	const assignInteractionFnMap = useMemo(
		() => ({
			[InteractionType.Call]: assignCall,
			[InteractionType.Generic]: assignGeneric,
			[InteractionType.Voicemail]: assignVoicemail,
			[InteractionType.Email]: assignEmail,
			[InteractionType.WhatsApp]: assignWhatsApp,
			[InteractionType.Webchat]: assignWebchat,
		}),
		[]
	);

	const afterAssignCallBack = useCallback(
		(assignResponse: AssignInteractionResponse) => {
			if (assignResponse.assigned) {
				return;
			}

			if (assignResponse.contactUnavailable) {
				showErrorMessage("ErrorMessage.Assign.ContactUnavailable");

				return;
			}

			if (assignResponse.contactNotFound) {
				showErrorMessage("ErrorMessage.Assign.ContactNotFound");

				return;
			}

			showErrorMessage("ErrorMessage.Assign.InteractionUnavailable");
		},
		[setSelectedTab, showErrorMessage]
	);

	const pickUpInteraction = useCallback(
		async (_: any, interaction: InteractionDto) => {
			if (await checkIsTeamsPresenceDnd(availability)) {
				showErrorMessage("ErrorMessage.Pickup.PickupDND");

				return;
			}

			handlePickUpInteraction(
				interaction.id,
				pickUpFnMap[interaction.type],
				afterPickUpCallBack,
				commonErrorCallBack
			);
		},
		[
			availability,
			showErrorMessage, //#warning-ts: to check if this is really necessary
			pickUpFnMap,
			afterPickUpCallBack,
			commonErrorCallBack,
		]
	);

	const assignInteraction = useCallback(
		(interaction: InteractionDto, agentTarget: string) => {
			handleAssignInteraction(
				{
					interactionId: interaction.id,
					agentTarget: agentTarget,
				},
				assignInteractionFnMap[interaction.type],
				afterAssignCallBack,
				commonErrorCallBack
			);
		},
		[
			showErrorMessage,
			assignInteractionFnMap,
			afterAssignCallBack,
			commonErrorCallBack,
		]
	);

	const unparkInteraction = useCallback(
		(interaction: InteractionDto) => {
			if (interaction.type == InteractionType.Call) {
				unParkCall(interaction.id).then(() => {
					setSelectedTab(TabType.Interactions);
				});
			} else {
				unParkTextInteraction(interaction.id, interaction.type).then(
					(value) => {
						if (value !== null) {
							setSelectedTab(TabType.Interactions);
						}
					}
				);
			}
		},
		[setSelectedTab]
	);

	const getTransferChannelType = (type: InteractionType) => {
		switch (type) {
			case InteractionType.Call:
				return TransferChannelType.Voice;
			case InteractionType.Email:
				return TransferChannelType.Email;
			case InteractionType.WhatsApp:
				return TransferChannelType.WhatsApp;
			case InteractionType.Webchat:
				return TransferChannelType.WebChat;
			case InteractionType.Voicemail:
				return TransferChannelType.Voicemail;
			default:
				return TransferChannelType.Email;
		}
	};

	const getAssignToAgentButton = useCallback(
		(interaction: InteractionDto) => {
			const transferChannelType = getTransferChannelType(interaction.type);

			return (
				<Flex>
					<PopoverView
						trigger={
							<TooltipWithMount
								key={"below"}
								withArrow
								positioning={"below"}
								content={{
									children: intl.formatMessage({
										id: "Interaction.Buttons.AssignToAgent",
									}),
									className: tableClasses.tooltip,
								}}
								relationship="label"
							>
								<Button
									appearance="primary"
									icon={<ArrowForwardDownPersonRegular />}
									className={classes.actionButton}
									onClick={(_) => {
										setAssignToAgentPopoverOpen(
											assignToAgentPopoverOpen === interaction.id
												? null
												: interaction.id
										);
									}}
								/>
							</TooltipWithMount>
						}
						renderOutsideTheDOM
						content={
							<AgentSearchDialog
								channelType={transferChannelType}
								actionButtonText={intl.formatMessage({
									id: "InteractionMenu.AssignToAgent",
								})}
								title={intl.formatMessage({
									id: "Interaction.Buttons.AssignToAgent",
								})}
								placeholder={intl.formatMessage({
									id: "Transfer.InputPlaceholder",
								})}
								onAction={(agentTarget: string) => {
									setAssignToAgentPopoverOpen(null);
									assignInteraction(interaction, agentTarget);
								}}
								onCancel={() => setAssignToAgentPopoverOpen(null)}
							/>
						}
						positioning={{ position: "below" }}
						open={assignToAgentPopoverOpen === interaction.id}
						onOpenChange={(_, data) => {
							if (!data.open) {
								setAssignToAgentPopoverOpen(null);
							}
						}}
					/>
				</Flex>
			);
		},
		[intl, classes, assignToAgentPopoverOpen, assignInteraction]
	);

	const getPickUpButton = useCallback(
		(interaction: InteractionDto) => {
			return (
				<Flex>
					<TooltipWithMount
						key={"below"}
						withArrow
						positioning={"below"}
						content={{
							children: intl.formatMessage({
								id: "Interaction.Buttons.PickUp",
							}),
							className: tableClasses.tooltip,
						}}
						relationship="label"
					>
						<Button
							appearance="primary"
							className={classes.actionButton}
							icon={<HandIcon />}
							onClick={(_) => pickUpInteraction(_, interaction)}
						/>
					</TooltipWithMount>
				</Flex>
			);
		},
		[pickUpInteraction, intl, classes]
	);

	const getResumeButton = useCallback(
		(interaction: InteractionDto) => {
			return (
				<Flex>
					<TooltipWithMount
						key={"below"}
						withArrow
						positioning={"below"}
						content={{
							children: intl.formatMessage({
								id: "Interaction.Buttons.Resume",
							}),
							className: tableClasses.tooltip,
						}}
						relationship="label"
					>
						<Button
							appearance="primary"
							className={classes.actionButton}
							icon={<Play20Regular />}
							onClick={(_) => unparkInteraction(interaction)}
						/>
					</TooltipWithMount>
				</Flex>
			);
		},
		[unparkInteraction, intl, tableClasses, classes]
	);

	const interactionIsAllowedAndCanBeResumed = useCallback(
		(interaction: InteractionDto) => {
			const interactionQueue = getQueue(interaction.queueId);

			if (!interactionQueue) {
				return false;
			}

			if (
				!interactionCanBeResumed(
					interaction,
					agentHasInteractionInOffering,
					agentHasCallInProgressOrTextInOffering
				) ||
				!(interactionQueue.isOperator && interactionQueue.hasParkingRight)
			) {
				return false;
			}
			return true;
		},
		[
			queues,
			agentHasInteractionInOffering,
			agentHasCallInProgressOrTextInOffering,
		]
	);

	const getTimerCell = useCallback(
		(interaction: InteractionDto) => (
			<StatusCell
				timer={
					interaction.type !== InteractionType.Voicemail ? (
						<Timer startDate={interaction?.startDate} />
					) : (
						<span>
							{formatDate(new Date(interaction.startDate), false, true)}
						</span>
					)
				}
				status={getInteractionStatusText(interaction.status, intl)}
			/>
		),
		[intl]
	);

	const getWaitingItemActions = useCallback(
		(item: InteractionDto) => {
			const buttons = [];

			if (
				interactionCanBePickedUp(
					{
						...item,
						isPickupEnabled: getQueue(item.queueId)?.isPickupEnabled ?? false,
					},
					agentHasInteractionInOffering,
					agentHasCallInProgressOrTextInOffering
				)
			) {
				buttons.push(getPickUpButton(item));
			}

			if (interactionIsAllowedAndCanBeResumed(item)) {
				buttons.push(getResumeButton(item));
			}

			if (interactionCanBeAssignedToAgent(item)) {
				buttons.push(getAssignToAgentButton(item));
			}

			if (buttons.length > 0) {
				return (
					<Flex gap={GapSize.Small} className={classes.buttonContainer}>
						{buttons}
					</Flex>
				);
			}

			return undefined;
		},
		[
			agentHasInteractionInOffering,
			agentHasCallInProgressOrTextInOffering,
			interactionCanBePickedUp,
			interactionIsAllowedAndCanBeResumed,
			interactionCanBeAssignedToAgent,
			queues,
		]
	);

	const renderTimerTableCell = useCallback(
		(item: InteractionDto, hoveredItemKey: string | null) => {
			if (hoveredItemKey == item.id || assignToAgentPopoverOpen === item.id) {
				const waitingItemActions = getWaitingItemActions(item);

				if (waitingItemActions) {
					return waitingItemActions;
				}
			}

			return getTimerCell(item);
		},
		[
			interactionIsAllowedAndCanBeResumed,
			getPickUpButton,
			getResumeButton,
			getAssignToAgentButton,
			getTimerCell,
			assignToAgentPopoverOpen,
			agentHasInteractionInOffering,
			interactionCanBePickedUp,
			interactionCanBeAssignedToAgent,
			queues,
		]
	);

	return {
		renderTimerTableCell,
		getWaitingItemActions,
	};
};
